import { useEffect, useState } from "react";

import { Controller, useForm } from "react-hook-form";
import { SlArrowDown, SlArrowUp } from "react-icons/sl";

import {
  OrgOption,
  fetchCommunityOptions,
  fetchGarretOptions,
  fetchLeafOptions,
} from "@/apis/organization";
import Button from "@/components/Button";
import Dropdown from "@/components/Dropdown/Dropdown";
import Input from "@/components/Input/Input";
import InputLabel from "@/components/Label/InputLabel";

const SignUpPreview = () => {
  const { register, control, watch, getValues, setValue } = useForm({
    defaultValues: {
      userName: "",
      telNo: "",
      cmt: { id: "", name: "" },
      gar: { id: "", name: "" },
      leaf: { id: "", name: "" },
      isAgree: "off",
    },
  });

  const [communityList, setCommunityList] = useState<OrgOption[]>([]);
  const [garretList, setGarretList] = useState<OrgOption[]>([]);
  const [leafList, setLeafList] = useState<OrgOption[]>([]);
  const [isDetailClick, setIsDetailClick] = useState(false);

  useEffect(() => {
    getCommunity();
  }, []);

  const resetList = (type: string) => {
    if (type === "cmt") {
      setCommunityList([]);
      setValue("cmt", { id: "", name: "" });
    } else if (type === "gar") {
      setGarretList([]);
      setValue("gar", { id: "", name: "" });
    } else if (type === "leaf") {
      setLeafList([]);
      setValue("leaf", { id: "", name: "" });
    }
  };

  useEffect(() => {
    resetList("gar");
    resetList("leaf");
    if (getValues("cmt").id !== "") {
      getGarret(getValues("cmt").id);
    }
  }, [watch("cmt")]);

  useEffect(() => {
    resetList("leaf");
    if (getValues("cmt").id !== "" && getValues("gar").id !== "") {
      getLeaf(getValues("cmt").id, getValues("gar").id);
    }
  }, [watch("gar")]);

  const getCommunity = async () => {
    setCommunityList(await fetchCommunityOptions());
  };

  const getGarret = async (cmtCd: string) => {
    setGarretList(await fetchGarretOptions(cmtCd));
  };

  const getLeaf = async (cmtCd: string, garCd: string) => {
    setLeafList(await fetchLeafOptions(cmtCd, garCd));
  };

  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800">
        관리자 미리보기 모드 — 실제 회원가입은 진행되지 않습니다.
      </div>

      <div className="relative w-[440px] shrink-0 rounded-2xl border border-gray-200 bg-gray-50 px-10 py-12 shadow-lg">
        <div className="flex w-full flex-col items-center">
          <div className="mb-4 flex flex-col gap-2 text-center">
            <h1 className="text-2xl font-bold">회원가입</h1>
            <p className="text-base text-gray-600">회원 정보를 입력해주세요.</p>
          </div>
          <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-col gap-2">
              <InputLabel
                text="이름"
                htmlFor="preview-userName"
                isRequired={true}
              />
              <Input
                id="preview-userName"
                type="text"
                placeholder="이름을 입력하세요"
                {...register("userName")}
                variant="default"
              />
              <p className="text-xs text-red-500">
                * 반드시 실명을 입력해주세요.
              </p>
            </div>
            <div className="flex w-full flex-col gap-2">
              <InputLabel
                text="전화번호"
                htmlFor="preview-telNo"
                isRequired={true}
              />
              <Input
                id="preview-telNo"
                type="text"
                placeholder="010-0000-0000"
                inputMode="numeric"
                maxLength={13}
                {...register("telNo", {
                  onChange: e => {
                    const raw = e.target.value
                      .replace(/[^0-9]/g, "")
                      .slice(0, 11);
                    let formatted = raw;
                    if (raw.length > 7) {
                      formatted = `${raw.slice(0, 3)}-${raw.slice(3, 7)}-${raw.slice(7)}`;
                    } else if (raw.length > 3) {
                      formatted = `${raw.slice(0, 3)}-${raw.slice(3)}`;
                    }
                    setValue("telNo", formatted);
                  },
                })}
                variant="default"
              />
            </div>
            <div className="flex w-full flex-col gap-2">
              <InputLabel
                text="소속"
                htmlFor="preview-belong"
                isRequired={true}
              />
              <div className="grid grid-cols-3 gap-2">
                <Controller
                  control={control}
                  name="cmt"
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      category="cmt"
                      options={communityList}
                      disabled={communityList.length === 0}
                      onChangeOption={onChange}
                      selectedOption={value.name}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="gar"
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      category="gar"
                      options={garretList}
                      disabled={garretList.length === 0}
                      onChangeOption={onChange}
                      selectedOption={value.name}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="leaf"
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      category="leaf"
                      options={leafList}
                      disabled={leafList.length === 0}
                      onChangeOption={onChange}
                      selectedOption={value.name}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex w-full flex-col gap-2">
              <div className="flex flex-col items-start justify-start">
                <Controller
                  control={control}
                  name="isAgree"
                  render={({ field: { onChange, value } }) => (
                    <div className="flex flex-row items-start gap-4 text-small">
                      <input
                        type="checkbox"
                        className="mt-1 scale-150 rounded border-2 border-gray-400 accent-blue-600"
                        checked={value === "on"}
                        disabled={!isDetailClick}
                        onChange={() => onChange(value === "on" ? "off" : "on")}
                      />
                      <div>
                        {isDetailClick ? (
                          <div
                            onClick={() => setIsDetailClick(false)}
                            className="flex cursor-pointer flex-row items-start"
                          >
                            <div>
                              <span className="font-bold text-primary">
                                [개인정보 수집과 이용에 대한 동의]{" "}
                              </span>
                              예약자 확인을 위한 개인정보 수집과 이용에
                              동의합니다.
                            </div>
                            <SlArrowUp
                              size={16}
                              className="ml-2 mt-1 py-0 text-xl text-gray-600"
                            />
                          </div>
                        ) : (
                          <div
                            onClick={() => setIsDetailClick(true)}
                            className="flex cursor-pointer flex-row items-start"
                          >
                            <div>
                              <span className="mb-1 block font-bold text-red-600">
                                [개인정보 수집과 이용에 대한 동의]{" "}
                              </span>
                              예약자 확인을 위한 개인정보 수집과 이용에
                              동의합니다.
                            </div>
                            <SlArrowDown
                              size={16}
                              className="ml-2 mt-1 py-0 text-xl text-gray-600"
                            />
                          </div>
                        )}

                        {isDetailClick && (
                          <>
                            <hr className="my-2 mr-5" />
                            <div className="leading-5">
                              - 수집 목적 : 예약자 확인 및 예약 정보 발송
                            </div>
                            <div className="leading-5">
                              - 수집 항목 : 이름, 휴대폰 번호, 소속, 카카오톡
                              프로필 정보
                            </div>
                            <div className="leading-5">
                              - 보유 및 이용 기간 : 회원 탈퇴 시까지
                            </div>
                            <hr className="my-2 mr-5" />
                          </>
                        )}
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>
          </div>

          <div className="mt-6 w-full">
            <Button variant="primary" type="button" disabled={true}>
              회원가입 (미리보기)
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPreview;
